import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import analytics from '../lib/analytics';

// Determine API base URL based on current hostname
const getBaseURL = () => {
  if (typeof window === 'undefined') return 'http://localhost:3001/api/v1';

  const host = window.location.hostname;
  const url = host.includes('lotngo.com')
    ? 'https://api.lotngo.com/api/v1'
    : 'http://localhost:3001/api/v1';

  console.log('[API Client] hostname:', host, 'baseURL:', url);
  return url;
};

const baseURL = getBaseURL();
export const apiClient = axios.create({
  baseURL,
});

// Attach Bearer token from in-memory store on every request
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Silent refresh on 401 — retry the original request once
let isRefreshing = false;
let pendingQueue: Array<(token: string) => void> = [];

apiClient.interceptors.response.use(
  (res) => res,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const original = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
    if (!original) return Promise.reject(error);

    // Auth endpoints must surface their own errors directly to the caller.
    // Running the silent-refresh path on /auth/login or /auth/refresh would:
    //   - swallow INVALID_CREDENTIALS (user sees a blank reload instead of
    //     "wrong password")
    //   - cause infinite recursion when /auth/refresh itself returns 401
    // The login/refresh routes are the *source* of session state, never consumers.
    const requestUrl = original.url ?? '';
    const isAuthEndpoint =
      requestUrl.endsWith('/auth/login') ||
      requestUrl.endsWith('/auth/refresh');

    // Track all API errors (4xx/5xx) once per request — skip silent-refresh 401s
    // which are handled below and shouldn't generate user-visible error events.
    const status = error.response?.status;
    if (status && status >= 400 && !(status === 401 && !original._retry && !isAuthEndpoint)) {
      const url = original.url ?? 'unknown';
      // Strip any UUIDs / IDs from the URL path to keep label cardinality low
      const endpoint = url.replace(/\/[0-9a-f-]{8,}/gi, '/:id');
      const errorCode = (error.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
      analytics.trackApiError(status, endpoint, errorCode);
    }

    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Queue this request until the refresh resolves
        return new Promise((resolve, reject) => {
          pendingQueue.push((token) => {
            original.headers = original.headers ?? {};
            original.headers['Authorization'] = `Bearer ${token}`;
            resolve(apiClient(original));
          });
          void error; // satisfy linter
          reject;     // kept for type shape — queue resolves via the push above
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        await useAuthStore.getState().refresh();
        const newToken = useAuthStore.getState().accessToken!;

        pendingQueue.forEach((cb) => cb(newToken));
        pendingQueue = [];

        original.headers = original.headers ?? {};
        original.headers['Authorization'] = `Bearer ${newToken}`;
        return apiClient(original);
      } catch (refreshError) {
        pendingQueue = [];

        // Distinguish hard auth failure (logout) from transient failure (keep session).
        //  - Server returned 401/403 → refresh token is truly invalid → logout.
        //  - No response (offline / DNS / TLS / timeout) → mobile network flap →
        //    keep tokens, just reject the original request. The caller (or the
        //    next request once network recovers) can retry.
        //  - 5xx → API is broken, not the user's session → same: don't logout.
        const isAuthRejection =
          axios.isAxiosError(refreshError) &&
          (refreshError.response?.status === 401 || refreshError.response?.status === 403);

        if (isAuthRejection) {
          useAuthStore.getState().logout();
        }

        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
